import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { ScrollArea } from '../components/ui/scroll-area';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { MatchHighlightsModal } from '../components/MatchHighlightsModal';
import { MatchSimModal } from '../components/MatchSimModal';
import { CompetitionAwards } from '../components/CompetitionAwards';
import { quickSimulate, updatePlayerStats, simulateKnockoutMatch } from '../utils/simulationEngine';
import { getPlayerStat } from '../utils/gameData';
import { buildWorldCupTeams, drawGroupsOfFour, isWCYear, nextWCYear } from '../utils/worldCup';
import { ArrowLeft, GlobeHemisphereWest, Lock, Lightning, Play, Eye, SoccerBall, ArrowsLeftRight, ShieldCheck, Cards, Trophy, Star } from '@phosphor-icons/react';

export const WorldCupPage = ({ gameState, onUpdateGameState }) => {
  const navigate = useNavigate();
  const teams = gameState?.teams || [];
  const year = gameState?.year || 2026;
  const wcTeams = gameState?.wcTeams || [];
  const [wcState, setWcState] = useState(() => gameState?.worldCup || null);
  const [highlightsMatch, setHighlightsMatch] = useState(null);
  const [simModalMatch, setSimModalMatch] = useState(null);

  const eligible = isWCYear(year);
  const next = nextWCYear(year);
  const allTeams = useMemo(() => wcTeams || [], [wcTeams]);
  const getTeam = (id) => allTeams.find(t => t.id === id);

  const startWC = () => {
    const built = buildWorldCupTeams(teams);
    const groups = drawGroupsOfFour(built);
    const newWc = { phase: 'group', groups, knockout: null };
    setWcState(newWc);
    onUpdateGameState({ ...gameState, worldCup: newWc, wcTeams: built });
  };

  const resetWC = () => {
    if (!window.confirm('Reset the World Cup? Progress will be lost.')) return;
    setWcState(null);
    onUpdateGameState({ ...gameState, worldCup: null });
  };

  const applyGroupMatch = (wc, gIdx, fIdx, home, away, result) => {
    const next = { ...wc, groups: [...wc.groups] };
    const g = { ...next.groups[gIdx] };
    g.fixtures = g.fixtures.map((f, i) => i === fIdx ? { ...f, homeScore: result.homeScore, awayScore: result.awayScore, played: true, events: result.events, matchRatings: result.matchRatings } : f);
    const st = { ...g.standings };
    st[home.id] = { ...st[home.id] }; st[away.id] = { ...st[away.id] };
    st[home.id].played++; st[home.id].gf += result.homeScore; st[home.id].ga += result.awayScore;
    st[away.id].played++; st[away.id].gf += result.awayScore; st[away.id].ga += result.homeScore;
    if (result.homeScore > result.awayScore) { st[home.id].won++; st[home.id].points += 3; st[away.id].lost++; }
    else if (result.homeScore < result.awayScore) { st[away.id].won++; st[away.id].points += 3; st[home.id].lost++; }
    else { st[home.id].drawn++; st[home.id].points++; st[away.id].drawn++; st[away.id].points++; }
    g.standings = st;
    g.completed = g.fixtures.every(f => f.played);
    next.groups[gIdx] = g;
    return next;
  };

  const persist = (newWc, result, home, away) => {
    const ewflUpd = updatePlayerStats(teams, result.events, home.name, away.name, result.homeScore, result.awayScore, 'worldCup');
    const wcUpd = updatePlayerStats(wcTeams, result.events, home.name, away.name, result.homeScore, result.awayScore, 'worldCup');
    setWcState(newWc);
    onUpdateGameState({ ...gameState, teams: ewflUpd, wcTeams: wcUpd, worldCup: newWc });
  };

  const simOneGroupMatch = (gIdx, fIdx) => {
    const g = wcState.groups[gIdx];
    const f = g.fixtures[fIdx];
    if (f.played) return;
    const home = getTeam(f.homeId), away = getTeam(f.awayId);
    if (!home || !away) return;
    const result = quickSimulate(home, away);
    const newWc = applyGroupMatch(wcState, gIdx, fIdx, home, away, result);
    persist(newWc, result, home, away);
  };

  const simAllGroups = () => {
    let wc = wcState, ewflUpd = teams, wcUpd = wcTeams;
    wc.groups.forEach((g, gi) => {
      g.fixtures.forEach((f, fi) => {
        if (f.played) return;
        const home = allTeams.find(t => t.id === f.homeId);
        const away = allTeams.find(t => t.id === f.awayId);
        if (!home || !away) return;
        const result = quickSimulate(home, away);
        wc = applyGroupMatch(wc, gi, fi, home, away, result);
        ewflUpd = updatePlayerStats(ewflUpd, result.events, home.name, away.name, result.homeScore, result.awayScore, 'worldCup');
        wcUpd = updatePlayerStats(wcUpd, result.events, home.name, away.name, result.homeScore, result.awayScore, 'worldCup');
      });
    });
    setWcState(wc);
    onUpdateGameState({ ...gameState, teams: ewflUpd, wcTeams: wcUpd, worldCup: wc });
  };

  const openFullGroup = (gIdx, fIdx) => {
    const g = wcState.groups[gIdx], f = g.fixtures[fIdx];
    const home = getTeam(f.homeId), away = getTeam(f.awayId);
    if (!home || !away || f.played) return;
    setSimModalMatch({ home, away, onApply: (result) => {
      const newWc = applyGroupMatch(wcState, gIdx, fIdx, home, away, result);
      persist(newWc, result, home, away);
    }});
  };

  const getStandings = (g) => [...g.teamIds]
    .map(id => ({ id, stats: g.standings[id] }))
    .sort((a, b) => {
      if (b.stats.points !== a.stats.points) return b.stats.points - a.stats.points;
      const gdA = a.stats.gf - a.stats.ga, gdB = b.stats.gf - b.stats.ga;
      if (gdB !== gdA) return gdB - gdA;
      return b.stats.gf - a.stats.gf;
    });

  const advanceToKnockout = () => {
    const qualifiers = [];
    wcState.groups.forEach(g => {
      const st = getStandings(g);
      qualifiers.push({ id: st[0].id, groupName: g.name, rank: 1 });
      qualifiers.push({ id: st[1].id, groupName: g.name, rank: 2 });
    });
    // Standard WC bracket: 1A vs 2B, 1C vs 2D, etc.
    const r16 = [];
    const pairs = [[0,3],[4,7],[8,11],[12,15],[2,1],[6,5],[10,9],[14,13]]; // 1st of group A vs 2nd of B, etc.
    const winners1st = qualifiers.filter(q => q.rank === 1);
    const winners2nd = qualifiers.filter(q => q.rank === 2);
    // Standard bracket pairings: 1A-2B, 1C-2D, 1E-2F, 1G-2H, 1B-2A, 1D-2C, 1F-2E, 1H-2G
    const order = [[0,1],[2,3],[4,5],[6,7], [1,0],[3,2],[5,4],[7,6]];
    order.forEach(([a, b]) => {
      r16.push({ homeId: winners1st[a].id, awayId: winners2nd[b].id, homeScore: null, awayScore: null, played: false, winnerId: null, events: null, matchRatings: null, pens: null });
    });
    setWcState({ ...wcState, phase: 'r16', knockout: { r16, qf: [], sf: [], final: [] } });
    onUpdateGameState({ ...gameState, worldCup: { ...wcState, phase: 'r16', knockout: { r16, qf: [], sf: [], final: [] } } });
  };

  const applyKoResult = (roundKey, idx, home, away, result) => {
    const newKO = { ...wcState.knockout, [roundKey]: [...wcState.knockout[roundKey]] };
    const winnerId = result.pens ? (result.pens.winner === 'home' ? home.id : away.id) : (result.homeScore > result.awayScore ? home.id : away.id);
    newKO[roundKey][idx] = { ...newKO[roundKey][idx], homeScore: result.homeScore, awayScore: result.awayScore, played: true, winnerId, events: result.events, matchRatings: result.matchRatings, pens: result.pens || null };
    const newWc = { ...wcState, knockout: newKO };
    if (newKO[roundKey].every(m => m.played)) {
      const winners = newKO[roundKey].map(m => m.winnerId);
      const nextRound = roundKey === 'r16' ? 'qf' : roundKey === 'qf' ? 'sf' : roundKey === 'sf' ? 'final' : null;
      if (nextRound && winners.length >= 2) {
        const nm = [];
        for (let i = 0; i < winners.length; i += 2) nm.push({ homeId: winners[i], awayId: winners[i + 1], homeScore: null, awayScore: null, played: false, winnerId: null, events: null, matchRatings: null, pens: null });
        newKO[nextRound] = nm;
        newWc.phase = nextRound;
      } else if (roundKey === 'final') {
        newWc.phase = 'complete';
        newWc.champion = winnerId;
      }
    }
    const ewflUpd = updatePlayerStats(teams, result.events, home.name, away.name, result.homeScore, result.awayScore, 'worldCup');
    const wcUpd = updatePlayerStats(wcTeams, result.events, home.name, away.name, result.homeScore, result.awayScore, 'worldCup');
    let trophyHistory = gameState.trophyHistory || [];
    if (newWc.phase === 'complete' && newWc.champion) {
      const champion = allTeams.find(t => t.id === newWc.champion);
      const already = trophyHistory.some(h => h.year === year && h.competition === 'worldCup');
      if (!already && champion) {
        trophyHistory = [...trophyHistory, { season: gameState.season, year, competition: 'worldCup', competitionLabel: 'FIFA World Cup', winnerId: champion.id, winnerName: champion.name }];
      }
    }
    setWcState(newWc);
    onUpdateGameState({ ...gameState, teams: ewflUpd, wcTeams: wcUpd, worldCup: newWc, trophyHistory });
  };

  const simKoOne = (roundKey, idx) => {
    const m = wcState.knockout[roundKey][idx];
    if (m.played) return;
    const home = getTeam(m.homeId), away = getTeam(m.awayId);
    if (!home || !away) return;
    const result = simulateKnockoutMatch(home, away, 0, 0);
    applyKoResult(roundKey, idx, home, away, result);
  };

  const openFullKo = (roundKey, idx) => {
    const m = wcState.knockout[roundKey][idx];
    if (m.played) return;
    const home = getTeam(m.homeId), away = getTeam(m.awayId);
    if (!home || !away) return;
    setSimModalMatch({ home, away, knockout: true, onApply: (result) => applyKoResult(roundKey, idx, home, away, result) });
  };

  // Stats aggregations
  const wcStats = useMemo(() => {
    const players = wcTeams.flatMap(t => t.players.map(p => ({ ...p, teamName: t.name })));
    return {
      scorers: [...players].filter(p => getPlayerStat(p, 'goals', 'worldCup') > 0).sort((a, b) => getPlayerStat(b, 'goals', 'worldCup') - getPlayerStat(a, 'goals', 'worldCup')).slice(0, 100),
      assists: [...players].filter(p => getPlayerStat(p, 'assists', 'worldCup') > 0).sort((a, b) => getPlayerStat(b, 'assists', 'worldCup') - getPlayerStat(a, 'assists', 'worldCup')).slice(0, 100),
      cs: [...players].filter(p => getPlayerStat(p, 'cleanSheets', 'worldCup') > 0).sort((a, b) => getPlayerStat(b, 'cleanSheets', 'worldCup') - getPlayerStat(a, 'cleanSheets', 'worldCup')).slice(0, 100),
      cards: [...players].filter(p => getPlayerStat(p, 'yellowCards', 'worldCup') > 0).sort((a, b) => getPlayerStat(b, 'yellowCards', 'worldCup') - getPlayerStat(a, 'yellowCards', 'worldCup')).slice(0, 100),
    };
  }, [wcTeams]);

  // EWFL players called up to World Cup
  const ewflCallups = useMemo(() =>
    wcTeams.flatMap(t => t.players.filter(p => p.ewflCallup).map(p => ({ ...p, nationTeam: t.name, nationFlag: t.flag })))
  , [wcTeams]);

  const MatchRow = ({ match, idx, onQuick, onFull }) => {
    const home = getTeam(match.homeId), away = getTeam(match.awayId);
    if (!home || !away) return null;
    return (
      <div className="flex items-center gap-2 text-xs p-1.5 bg-[#0A0A0C] rounded-sm" data-testid={`wc-match-${idx}`}>
        <span className="text-white flex-1 truncate text-right">{home.flag} {home.name}</span>
        {match.played ? <span className="font-mono font-bold text-white px-2">{match.homeScore}-{match.awayScore}{match.pens && ` (${match.pens.home}-${match.pens.away} pens)`}</span> : <span className="text-[#6E6E73] px-2">vs</span>}
        <span className="text-white flex-1 truncate">{away.flag} {away.name}</span>
        {!match.played ? (
          <div className="flex gap-1">
            <Button size="sm" onClick={onQuick} className="bg-[#AF52DE] hover:bg-[#AF52DE]/80 text-white h-6 text-[10px] px-2">Quick</Button>
            <Button size="sm" onClick={onFull} className="bg-[#007AFF] hover:bg-[#0A84FF] text-white h-6 text-[10px] px-2">Full</Button>
          </div>
        ) : (
          <Button size="sm" variant="outline" onClick={() => setHighlightsMatch({ match: { ...match, homeTeamName: home.name, awayTeamName: away.name }, homeTeam: home, awayTeam: away })} className="h-6 text-[10px] px-2 bg-transparent border-[#2C2C35] text-white">
            <Eye size={10} className="mr-1" /> Stats
          </Button>
        )}
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-[#04060F] pt-20 pb-8 px-4">
      <div className="max-w-7xl mx-auto">
        <div className="flex items-center gap-4 mb-6">
          <Button onClick={() => navigate('/')} variant="ghost" className="text-[#A0A0A5] hover:text-white" data-testid="wc-back-btn"><ArrowLeft size={20} /></Button>
          <div className="flex-1">
            <h1 className="font-heading font-black text-3xl sm:text-4xl text-white uppercase tracking-wide flex items-center gap-3">
              <GlobeHemisphereWest size={32} className="text-[#AF52DE]" weight="fill" /> FIFA World Cup
            </h1>
            <p className="text-[#A0A0A5] mt-1">32 nations • 8 groups of 4 • Every 4 years starting 2026</p>
          </div>
          {wcState && <Button variant="outline" onClick={resetWC} className="bg-transparent border-[#FF3B30] text-[#FF3B30]" data-testid="reset-wc-btn">Reset</Button>}
        </div>

        {!eligible && !wcState ? (
          <div className="card-surface rounded-sm p-12 text-center">
            <Lock size={64} className="text-[#6E6E73] mx-auto mb-4" />
            <h2 className="font-heading font-bold text-2xl text-white uppercase mb-2">World Cup Not This Year</h2>
            <p className="text-[#A0A0A5] mb-4">The FIFA World Cup runs every 4 years starting 2026.</p>
            <p className="font-mono text-sm text-[#FF3B30] mt-4">Current Year: {year}</p>
            <p className="font-mono text-sm text-[#32D74B] mt-1">Next WC: {next}</p>
          </div>
        ) : !wcState ? (
          <div className="card-surface rounded-sm p-8 text-center">
            <GlobeHemisphereWest size={64} weight="fill" className="text-[#AF52DE] mx-auto mb-4" />
            <h2 className="font-heading font-bold text-2xl text-white uppercase mb-4">FIFA World Cup {year}</h2>
            <p className="text-[#A0A0A5] mb-6">32 national teams. Top EWFL performers get called up to their national squads.</p>
            <Button data-testid="start-wc-btn" onClick={startWC} className="bg-[#AF52DE] hover:bg-[#AF52DE]/80 text-white font-bold px-8">
              <Play size={18} className="mr-2" weight="fill" /> Draw Groups & Start
            </Button>
          </div>
        ) : (
          <Tabs defaultValue="main" className="w-full">
            <TabsList className="bg-[#121216] border border-[#2C2C35] w-full grid grid-cols-3 mb-6">
              <TabsTrigger value="main" className="data-[state=active]:bg-[#AF52DE] data-[state=active]:text-white font-heading uppercase text-sm" data-testid="wc-tab-main">Tournament</TabsTrigger>
              <TabsTrigger value="callups" className="data-[state=active]:bg-[#FFD700] data-[state=active]:text-black font-heading uppercase text-sm" data-testid="wc-tab-callups">EWFL Call-Ups</TabsTrigger>
              <TabsTrigger value="awards" className="data-[state=active]:bg-[#FF9500] data-[state=active]:text-black font-heading uppercase text-sm" data-testid="wc-tab-awards">Awards</TabsTrigger>
            </TabsList>

            <TabsContent value="main">
              <div className="space-y-6">
                {wcState.phase === 'group' && (
                  <div>
                    <div className="flex items-center justify-between mb-3">
                      <h2 className="font-heading font-bold text-xl text-white uppercase">Group Stage</h2>
                      <div className="flex gap-2">
                        {/* Sim All Groups removed — full sim each game individually */}
                        {wcState.groups.every(g => g.completed) && !wcState.knockout && <Button onClick={advanceToKnockout} className="bg-[#FFD700] hover:bg-[#FFD700]/80 text-black font-bold" size="sm" data-testid="wc-advance-ko"><Trophy size={16} className="mr-1" /> Advance to R16</Button>}
                      </div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {wcState.groups.map((g, gi) => {
                        const st = getStandings(g);
                        return (
                          <div key={g.name} className="card-surface rounded-sm" data-testid={`wc-group-${g.name}`}>
                            <div className="border-b border-[#2C2C35] px-4 py-2">
                              <h3 className="font-heading font-bold text-white">Group {g.name}</h3>
                            </div>
                            <Table>
                              <TableHeader>
                                <TableRow className="hover:bg-transparent border-b border-[#2C2C35]">
                                  <TableHead className="text-[#6E6E73] font-mono text-xs">Team</TableHead>
                                  <TableHead className="text-[#6E6E73] font-mono text-xs text-center w-8">P</TableHead>
                                  <TableHead className="text-[#6E6E73] font-mono text-xs text-center w-8">W</TableHead>
                                  <TableHead className="text-[#6E6E73] font-mono text-xs text-center w-8">D</TableHead>
                                  <TableHead className="text-[#6E6E73] font-mono text-xs text-center w-8">L</TableHead>
                                  <TableHead className="text-[#6E6E73] font-mono text-xs text-center w-10">GD</TableHead>
                                  <TableHead className="text-[#6E6E73] font-mono text-xs text-center w-10">Pts</TableHead>
                                </TableRow>
                              </TableHeader>
                              <TableBody>
                                {st.map((row, idx) => {
                                  const team = getTeam(row.id);
                                  const gd = row.stats.gf - row.stats.ga;
                                  return (
                                    <TableRow key={row.id} className={`border-b border-[#2C2C35] ${idx < 2 ? 'border-l-2 border-l-[#32D74B]' : ''} ${idx % 2 === 0 ? 'bg-[#0A0A0C]' : 'bg-[#121216]'}`}>
                                      <TableCell className="text-sm text-white truncate">{team?.flag} {team?.name}</TableCell>
                                      <TableCell className="font-mono text-xs text-center text-[#A0A0A5]">{row.stats.played}</TableCell>
                                      <TableCell className="font-mono text-xs text-center text-[#32D74B]">{row.stats.won}</TableCell>
                                      <TableCell className="font-mono text-xs text-center text-[#A0A0A5]">{row.stats.drawn}</TableCell>
                                      <TableCell className="font-mono text-xs text-center text-[#FF3B30]">{row.stats.lost}</TableCell>
                                      <TableCell className={`font-mono text-xs text-center ${gd > 0 ? 'text-[#32D74B]' : gd < 0 ? 'text-[#FF3B30]' : 'text-[#A0A0A5]'}`}>{gd > 0 ? `+${gd}` : gd}</TableCell>
                                      <TableCell className="font-mono text-xs text-center font-bold text-white">{row.stats.points}</TableCell>
                                    </TableRow>
                                  );
                                })}
                              </TableBody>
                            </Table>
                            <div className="p-2 space-y-1 border-t border-[#2C2C35]">
                              {g.fixtures.map((f, fi) => <MatchRow key={f.id} match={f} idx={`g${g.name}-${fi}`} onQuick={() => simOneGroupMatch(gi, fi)} onFull={() => openFullGroup(gi, fi)} />)}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {wcState.knockout && ['r16', 'qf', 'sf', 'final'].map(rk => {
                  const round = wcState.knockout[rk];
                  if (!round || round.length === 0) return null;
                  const label = rk === 'r16' ? 'Round of 16' : rk === 'qf' ? 'Quarter-Finals' : rk === 'sf' ? 'Semi-Finals' : 'FINAL';
                  const cols = rk === 'final' ? 'grid-cols-1 max-w-md mx-auto' : 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-4';
                  return (
                    <div key={rk}>
                      <h2 className="font-heading font-bold text-xl text-white uppercase mb-3">{label}</h2>
                      <div className={`grid gap-3 ${cols}`}>
                        {round.map((m, i) => (
                          <div key={i} className="card-elevated rounded-sm p-3">
                            <MatchRow match={m} idx={`${rk}-${i}`} onQuick={() => simKoOne(rk, i)} onFull={() => openFullKo(rk, i)} />
                            {m.winnerId && <p className="text-[10px] text-[#32D74B] mt-1">→ {getTeam(m.winnerId)?.flag} {getTeam(m.winnerId)?.name}</p>}
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })}

                {wcState.phase === 'complete' && wcState.champion && (
                  <div className="card-surface rounded-sm p-8 text-center">
                    <GlobeHemisphereWest size={64} weight="fill" className="text-[#FFD700] mx-auto mb-3" />
                    <h2 className="font-heading font-black text-3xl text-[#FFD700] uppercase">World Cup Champions!</h2>
                    <p className="text-white text-2xl mt-2">{getTeam(wcState.champion)?.flag} {getTeam(wcState.champion)?.name}</p>
                  </div>
                )}
              </div>
            </TabsContent>

            <TabsContent value="callups">
              <div className="card-surface rounded-sm">
                <div className="border-b border-[#2C2C35] px-4 py-3 flex items-center gap-2">
                  <Star size={18} className="text-[#FFD700]" weight="fill" />
                  <h3 className="font-heading font-bold text-white uppercase tracking-wide">EWFL Players Called Up to World Cup</h3>
                </div>
                <ScrollArea className="h-[550px]">
                  {ewflCallups.length === 0 ? (
                    <p className="text-[#6E6E73] text-sm p-6 text-center">No EWFL players called up yet.</p>
                  ) : (
                    <div className="p-3 space-y-2">
                      {ewflCallups.map((p, i) => (
                        <div key={p.id} className="card-elevated rounded-sm p-3 flex items-center gap-3" data-testid={`wc-callup-${i}`}>
                          <div className="text-center">
                            <span className="text-2xl">{p.nationFlag}</span>
                            <p className="text-[10px] text-[#A0A0A5] uppercase mt-1">{p.nationTeam}</p>
                          </div>
                          <div className="flex-1">
                            <p className="text-sm font-bold text-white">{p.name}</p>
                            <div className="flex gap-3 text-[10px] text-[#6E6E73]">
                              <span>{p.position}</span>
                              <span>OVR {p.overall}</span>
                              <span>Age {p.age}</span>
                              <span className="text-[#FFD700]">From: {p.fromClub}</span>
                            </div>
                          </div>
                          <div className="flex gap-3 text-xs">
                            <span className="text-[#FF3B30]">⚽ {getPlayerStat(p, 'goals', 'worldCup')}</span>
                            <span className="text-[#007AFF]">🅰 {getPlayerStat(p, 'assists', 'worldCup')}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </ScrollArea>
              </div>
            </TabsContent>

            <TabsContent value="awards">
              <CompetitionAwards teams={gameState?.wcTeams || []} competition="worldCup" topN={25} />
            </TabsContent>
          </Tabs>
        )}
      </div>

      <MatchHighlightsModal isOpen={!!highlightsMatch} onClose={() => setHighlightsMatch(null)} match={highlightsMatch?.match} homeTeam={highlightsMatch?.homeTeam} awayTeam={highlightsMatch?.awayTeam} competitionLabel="FIFA World Cup" />
      <MatchSimModal isOpen={!!simModalMatch} onClose={() => setSimModalMatch(null)} homeTeam={simModalMatch?.home} awayTeam={simModalMatch?.away} competitionLabel="FIFA World Cup" knockout={simModalMatch?.knockout || false} onResult={(result) => { simModalMatch?.onApply?.(result); }} />
    </div>
  );
};

const StatList = ({ label, data, statKey, color, icon: Icon }) => (
  <div className="card-surface rounded-sm">
    <div className="border-b border-[#2C2C35] px-4 py-3 flex items-center gap-2">
      <Icon size={18} className={color} weight="fill" />
      <h3 className="font-heading font-bold text-white uppercase tracking-wide">Top 100 {label}</h3>
    </div>
    <ScrollArea className="h-[500px]">
      {data.length === 0 ? (
        <p className="text-[#6E6E73] text-sm p-6 text-center">No World Cup stats yet.</p>
      ) : (
        <div className="p-2">
          {data.map((p, i) => (
            <div key={p.id} className={`flex items-center gap-2 px-2 py-1.5 ${i % 2 === 0 ? 'bg-[#0A0A0C]' : 'bg-[#121216]'}`}>
              <span className={`font-mono text-sm font-bold w-6 ${i === 0 ? 'text-[#FFD700]' : 'text-[#A0A0A5]'}`}>{i + 1}</span>
              <span className="nationality-flag text-sm">{p.flag}</span>
              <span className="text-sm text-white flex-1 truncate">{p.name}</span>
              <span className="text-xs text-[#6E6E73] truncate max-w-[100px]">{p.teamName}</span>
              <span className={`font-mono text-sm font-bold ${color}`}>{getPlayerStat(p, statKey, 'worldCup')}</span>
            </div>
          ))}
        </div>
      )}
    </ScrollArea>
  </div>
);
